import { useState } from 'react';
import { useUpdateProfile } from '../../api/queries/auth';
import type { User } from '../../api/types';
import { Modal } from '../../components/Modal';
import { PasswordField } from '../../components/PasswordField';
import { useT } from '../../i18n';
import { useRecheckOnLanguage, useValidators } from '../../lib/validators';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../providers/ToastProvider';

export function EditProfileModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useT();
  const { setUser } = useAuth();
  const update = useUpdateProfile();
  const toast = useToast();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email ?? '');
  const [address, setAddress] = useState(user.address ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  /**
   * Same rule as the sign-in and sign-up forms: flag on submit, clear the moment
   * the entry becomes correct. This form used to check nothing but the name, so
   * a malformed address travelled to the server and came back as a toast that
   * named no field — or, worse, was caught by the browser's own bubble, which is
   * the chrome the rest of the app deliberately renders itself.
   */
  const v = useValidators();
  type Field = 'name' | 'email';
  const [errors, setErrors] = useState<Partial<Record<Field, string | null>>>({});
  const check = (field: Field, value: string) =>
    field === 'name' ? v.name(value, t('name')) : v.optionalEmail(value);
  const revalidate = (field: Field, value: string) =>
    setErrors((e) => (e[field] ? { ...e, [field]: check(field, value) } : e));
  useRecheckOnLanguage(setErrors, () => ({ name: check('name', name), email: check('email', email) }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const found = { name: check('name', name), email: check('email', email) };
    if (found.name || found.email) return setErrors(found);
    setErrors({});

    if (newPassword && !currentPassword) return toast(t('epCurrentRequired'), 'warn');

    try {
      const { user: saved } = await update.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        address: address.trim(),
        // Only sent when the user is actually changing the password.
        ...(newPassword ? { currentPassword, newPassword } : {}),
      });
      setUser(saved);
      toast(t('epSaved'), 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  };

  return (
    <Modal title={t('menuEditDetails')} onClose={onClose} maxWidth={440}>
      {/* noValidate for the same reason as every other form here: the browser's
          own validation bubble is chrome the app cannot style or translate. */}
      <form onSubmit={submit} noValidate>
        <label>{t('name')}</label>
        <input
          className={errors.name ? 'invalid' : ''}
          aria-invalid={Boolean(errors.name)}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            revalidate('name', e.target.value);
          }}
        />
        {errors.name && <span className="field-error">{errors.name}</span>}

        <label>{t('email')}</label>
        <input
          type="email"
          placeholder="you@example.com"
          className={errors.email ? 'invalid' : ''}
          aria-invalid={Boolean(errors.email)}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            revalidate('email', e.target.value);
          }}
        />
        {errors.email && <span className="field-error">{errors.email}</span>}

        <label>{t('authAddress')}</label>
        <input placeholder={t('authAddressPlaceholder')} value={address} onChange={(e) => setAddress(e.target.value)} />

        <label>{t('phone')}</label>
        {/* The login credential, OTP-verified at signup — not editable here. */}
        <input value={user.phone} disabled />
        <span className="field-note">{t('epPhoneFixed')}</span>

        <h4 className="form-divider">{t('epChangePassword')}</h4>
        <label>{t('epCurrentPassword')}</label>
        <PasswordField
          autoComplete="current-password"
          ariaLabel={t('epCurrentPassword')}
          value={currentPassword}
          onChange={setCurrentPassword}
        />
        <label>{t('epNewPassword')}</label>
        <PasswordField
          autoComplete="new-password"
          ariaLabel={t('epNewPassword')}
          placeholder={t('epNewPasswordHint')}
          value={newPassword}
          onChange={setNewPassword}
        />

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn" disabled={update.isPending}>
            {update.isPending ? t('saving') : t('epSaveChanges')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
